import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Chip,
  DesignV2Scope,
  EmptyState,
  Sheet,
  Skeleton,
  StatusPill,
  SelectableCard,
  StickyBottomActions,
} from "../design-v2";
import { StackHeader } from "../components/product";
import { useManualProgram } from "../context/ManualProgramContext";
import { useMultiWeekProgram } from "../context/MultiWeekProgramContext";
import { getCycleBuilderPath } from "../features/multiWeek/routes";
import { getManualBuilderPath } from "../features/weeklyPlans/routes";
import {
  createCycleFromWeeklyPlan,
  getCycleStartAvailability,
  openOrCreateWeeklyPlanEditDraft,
} from "../services/api";
import "./manualConvert.css";

const AVAILABILITY_SEARCH_BATCH_SIZE = 12;
// No shorter scheduling horizon exists in the product. Ten years keeps the
// search finite while covering 520 consecutive weekly start candidates.
const MAX_AVAILABILITY_SEARCH_WEEKS = 520;
const AVAILABILITY_FAILURE_MESSAGE =
  "Unable to check Cycle availability. Retry before choosing a start week.";

const WEEKDAY_ROWS = [
  { day: "MONDAY", shortLabel: "M", fullLabel: "Monday" },
  { day: "TUESDAY", shortLabel: "T", fullLabel: "Tuesday" },
  { day: "WEDNESDAY", shortLabel: "W", fullLabel: "Wednesday" },
  { day: "THURSDAY", shortLabel: "T", fullLabel: "Thursday" },
  { day: "FRIDAY", shortLabel: "F", fullLabel: "Friday" },
  { day: "SATURDAY", shortLabel: "S", fullLabel: "Saturday" },
  { day: "SUNDAY", shortLabel: "S", fullLabel: "Sunday" },
];

function formatDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateInput(value) {
  return new Date(`${value}T00:00:00`);
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addWeeks(date, weeks) {
  const next = new Date(date);
  next.setDate(next.getDate() + weeks * 7 - 1);
  return next;
}

function getTodayDateInput() {
  return formatDateInput(new Date());
}

function getNextMondayDateInput(dateValue = getTodayDateInput()) {
  const date = new Date(`${dateValue}T00:00:00`);
  const day = date.getDay();
  const daysUntilMonday = day === 1 ? 0 : day === 0 ? 1 : 8 - day;
  date.setDate(date.getDate() + daysUntilMonday);
  return formatDateInput(date);
}

function getCurrentWeekMondayDateInput(dateValue = getTodayDateInput()) {
  const date = new Date(`${dateValue}T00:00:00`);
  const day = date.getDay();
  const daysSinceMonday = day === 0 ? 6 : day - 1;
  date.setDate(date.getDate() - daysSinceMonday);
  return formatDateInput(date);
}

function getStartOfMonthDate(dateValue) {
  const date = dateValue instanceof Date ? new Date(dateValue) : parseDateInput(dateValue);
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(dateValue, months) {
  const date = dateValue instanceof Date ? new Date(dateValue) : parseDateInput(dateValue);
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function formatDisplayDate(value) {
  if (!value) {
    return "--";
  }

  return parseDateInput(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatMonthHeading(dateValue) {
  return dateValue.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function formatWeekRange(startDateValue) {
  const startDate = parseDateInput(startDateValue);
  const endDate = addDays(startDate, 6);
  const sameMonth = startDate.getMonth() === endDate.getMonth();
  const sameYear = startDate.getFullYear() === endDate.getFullYear();

  const startLabel = startDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const endLabel = endDate.toLocaleDateString("en-US", {
    month: sameMonth ? undefined : "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });

  return `${startLabel} to ${endLabel}`;
}

function buildWeekOptionsForMonth(monthDate, minStartDateValue, selectedStartDateValue = null) {
  const minStartDate = parseDateInput(minStartDateValue);
  const monthStart = getStartOfMonthDate(monthDate);
  const monthEnd = addDays(addMonths(monthStart, 1), -1);
  const firstWeekStart = parseDateInput(getNextMondayDateInput(formatDateInput(addDays(monthStart, -1))));
  const monthIndex = monthStart.getMonth();
  const year = monthStart.getFullYear();
  const normalizedSelectedValue =
    selectedStartDateValue && isMondayDateInput(selectedStartDateValue)
      ? selectedStartDateValue
      : null;
  const options = [];

  for (let cursor = firstWeekStart; cursor <= monthEnd; cursor = addDays(cursor, 7)) {
    const optionValue = formatDateInput(cursor);
    const mondayBelongsToDisplayedMonth =
      cursor.getMonth() === monthIndex && cursor.getFullYear() === year;
    const isSelected = optionValue === normalizedSelectedValue;

    if (optionValue < minStartDateValue || (!mondayBelongsToDisplayedMonth && !isSelected)) {
      continue;
    }

    options.push({
      value: optionValue,
      label: formatWeekRange(optionValue),
      monthLabel: formatMonthHeading(cursor),
      isCurrentWeek: optionValue === formatDateInput(minStartDate),
      isSelectedOutsideMonth: isSelected && !mondayBelongsToDisplayedMonth,
    });
  }

  if (
    normalizedSelectedValue &&
    !options.some((option) => option.value === normalizedSelectedValue)
  ) {
    const selectedDate = parseDateInput(normalizedSelectedValue);
    if (normalizedSelectedValue >= minStartDateValue) {
      options.unshift({
        value: normalizedSelectedValue,
        label: formatWeekRange(normalizedSelectedValue),
        monthLabel: formatMonthHeading(selectedDate),
        isCurrentWeek: normalizedSelectedValue === formatDateInput(minStartDate),
        isSelectedOutsideMonth: true,
      });
    }
  }

  return options;
}

function hasAllowedWeeksInMonth(monthDate, minStartDateValue) {
  return buildWeekOptionsForMonth(monthDate, minStartDateValue).length > 0;
}

function isMondayDateInput(value) {
  if (!value) {
    return false;
  }

  return new Date(`${value}T00:00:00`).getDay() === 1;
}

function buildConsecutiveMondayCandidates(startDateValue, offset, count) {
  const startDate = parseDateInput(startDateValue);
  return Array.from({ length: count }, (_, index) =>
    formatDateInput(addDays(startDate, (offset + index) * 7))
  );
}

function mapVerifiedAvailability(response, requestedStartDates) {
  const availability = Object.fromEntries(
    (response?.candidates || []).map((candidate) => [
      candidate.startDate,
      candidate,
    ])
  );
  const hasCompleteResponse = requestedStartDates.every(
    (startDate) => typeof availability[startDate]?.hasConflict === "boolean"
  );

  if (!hasCompleteResponse) {
    throw new Error("Cycle availability response was incomplete.");
  }

  return availability;
}

async function findAvailableCycleStart({
  durationWeeks,
  minStartDate,
  preferredStartDate,
  isCancelled,
}) {
  const accumulatedAvailability = {};
  const normalizedPreferredStartDate =
    isMondayDateInput(preferredStartDate) && preferredStartDate >= minStartDate
      ? preferredStartDate
      : null;
  let offset = 0;

  while (offset < MAX_AVAILABILITY_SEARCH_WEEKS) {
    const remainingCandidates = MAX_AVAILABILITY_SEARCH_WEEKS - offset;
    const batchCount = Math.min(AVAILABILITY_SEARCH_BATCH_SIZE, remainingCandidates);
    const consecutiveCandidates = buildConsecutiveMondayCandidates(
      minStartDate,
      offset,
      batchCount
    );
    const requestedStartDates = [...consecutiveCandidates];

    if (
      offset === 0 &&
      normalizedPreferredStartDate &&
      !requestedStartDates.includes(normalizedPreferredStartDate)
    ) {
      consecutiveCandidates.pop();
      requestedStartDates.pop();
      requestedStartDates.push(normalizedPreferredStartDate);
    }

    const response = await getCycleStartAvailability({
      candidateStartDates: requestedStartDates,
      durationWeeks,
    });
    if (isCancelled()) {
      return null;
    }

    const batchAvailability = mapVerifiedAvailability(response, requestedStartDates);
    Object.assign(accumulatedAvailability, batchAvailability);

    if (
      normalizedPreferredStartDate &&
      batchAvailability[normalizedPreferredStartDate]?.hasConflict === false
    ) {
      return {
        availability: accumulatedAvailability,
        startDate: normalizedPreferredStartDate,
      };
    }

    const firstAvailableStartDate = consecutiveCandidates.find(
      (startDate) => batchAvailability[startDate]?.hasConflict === false
    );
    if (firstAvailableStartDate) {
      return {
        availability: accumulatedAvailability,
        startDate: firstAvailableStartDate,
      };
    }

    offset += consecutiveCandidates.length;
  }

  const error = new Error(
    "No available Cycle start was found in the next 10 years. Review scheduled Cycles and retry."
  );
  error.code = "AVAILABILITY_SEARCH_EXHAUSTED";
  throw error;
}

function buildInitialWeekdaySlots(workouts = []) {
  const orderedWorkouts = [...workouts].map((workout, index) => ({
    ...workout,
    orderIndex: workout.orderIndex ?? index + 1,
  }));

  return WEEKDAY_ROWS.map((entry, index) => ({
    ...entry,
    workout: orderedWorkouts[index]
      ? {
          id: orderedWorkouts[index].id,
          name: orderedWorkouts[index].name,
          orderIndex: orderedWorkouts[index].orderIndex,
          blocks: orderedWorkouts[index].blocks || [],
        }
      : null,
  }));
}

function moveWorkoutWithDownwardPush(slots, sourceIndex, direction) {
  const targetIndex = sourceIndex + direction;
  if (
    sourceIndex < 0 ||
    sourceIndex >= slots.length ||
    targetIndex < 0 ||
    targetIndex >= slots.length ||
    !slots[sourceIndex]?.workout
  ) {
    return null;
  }

  const nextSlots = slots.map((slot) => ({ ...slot }));
  const movingWorkout = nextSlots[sourceIndex].workout;
  nextSlots[sourceIndex] = {
    ...nextSlots[sourceIndex],
    workout: null,
  };

  let displacedWorkout = movingWorkout;
  for (let index = targetIndex; index < nextSlots.length; index += 1) {
    const existingWorkout = nextSlots[index].workout;
    nextSlots[index] = {
      ...nextSlots[index],
      workout: displacedWorkout,
    };
    displacedWorkout = existingWorkout;

    if (!displacedWorkout) {
      return nextSlots;
    }
  }

  return null;
}

/* Feature-local inline glyphs. Design V2 has no icon dependency: every glyph is
 * an inline stroke SVG inheriting `currentColor`, exactly as the Phase 9A
 * surfaces do. */
function ConvertIcon() {
  return (
    <svg className="lz-manual-convert__glyph" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 9v6M6 7v10M18 7v10M21 9v6M6 12h12" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg className="lz-manual-convert__glyph" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

function CalendarCheckIcon() {
  return (
    <svg className="lz-manual-convert__glyph" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4M9 15l2 2 4-4" />
    </svg>
  );
}

function ChevronIcon({ direction }) {
  const paths = {
    down: "M6 9l6 6 6-6",
    left: "M15 18l-6-6 6-6",
    right: "M9 6l6 6-6 6",
    up: "M6 15l6-6 6 6",
  };

  return (
    <svg className="lz-manual-convert__glyph" viewBox="0 0 24 24" aria-hidden="true">
      <path d={paths[direction]} />
    </svg>
  );
}

function ArrowIcon({ direction }) {
  return (
    <svg className="lz-manual-convert__glyph" viewBox="0 0 24 24" aria-hidden="true">
      <path d={direction === "up" ? "M12 19V5M6 11l6-6 6 6" : "M12 5v14M6 13l6 6 6-6"} />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg className="lz-manual-convert__glyph" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </svg>
  );
}

export default function ManualConvert() {
  const navigate = useNavigate();
  const {
    programDraft,
    draftMetadata,
    beginHydrationTarget: beginWeeklyHydrationTarget,
    hydrateProgramDraft: hydrateWeeklyProgramDraft,
  } = useManualProgram();
  const { beginHydrationTarget, hydrateProgramDraft } = useMultiWeekProgram();

  const programName = programDraft.programName || "New Program";
  const sessionsPerWeek = programDraft.sessionsPerWeek || 4;
  const todayDate = getTodayDateInput();
  const initialPreferredStartDate = isMondayDateInput(programDraft.startDate)
    ? programDraft.startDate
    : null;
  const initialCalendarStartDate =
    initialPreferredStartDate || getNextMondayDateInput(programDraft.startDate || todayDate);
  const initialProgramLength = programDraft.programLength || 8;
  const minStartDate = useMemo(() => getCurrentWeekMondayDateInput(todayDate), [todayDate]);
  const templateWorkouts = useMemo(
    () =>
      [...(programDraft.workouts || [])].map((workout, index) => ({
        ...workout,
        orderIndex: workout.orderIndex ?? index + 1,
      })),
    [programDraft.workouts]
  );

  const [startDate, setStartDate] = useState("");
  const [programLength, setProgramLength] = useState(initialProgramLength);
  const [weekdaySlots, setWeekdaySlots] = useState(() =>
    buildInitialWeekdaySlots(programDraft.workouts || [])
  );
  const [isWeekPickerOpen, setIsWeekPickerOpen] = useState(false);
  const [weekPickerMonth, setWeekPickerMonth] = useState(() =>
    getStartOfMonthDate(initialCalendarStartDate)
  );
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availabilityByStartDate, setAvailabilityByStartDate] = useState({});
  const [availabilityStatus, setAvailabilityStatus] = useState("loading");
  const [availabilityError, setAvailabilityError] = useState("");
  const [availabilityRefreshToken, setAvailabilityRefreshToken] = useState(0);
  const [isVisibleAvailabilityLoading, setIsVisibleAvailabilityLoading] = useState(false);
  const [isReturningToBuilder, setIsReturningToBuilder] = useState(false);
  const [backError, setBackError] = useState("");
  const startDateRef = useRef("");
  const initialPreferredStartDateRef = useRef(initialPreferredStartDate);
  const availabilityByStartDateRef = useRef({});
  const isReturningToBuilderRef = useRef(false);

  const previewText = useMemo(() => {
    return `This ${programLength}-week program will duplicate your ${sessionsPerWeek}-session weekly template across all weeks.`;
  }, [programLength, sessionsPerWeek]);

  const endDate = useMemo(
    () =>
      startDate
        ? formatDateInput(addWeeks(new Date(`${startDate}T00:00:00`), programLength))
        : "",
    [programLength, startDate]
  );
  const weekdayAssignmentError = useMemo(() => {
    if (templateWorkouts.length === 0) {
      return "This weekly template has no workouts to convert.";
    }
  
    if (templateWorkouts.length > WEEKDAY_ROWS.length) {
      return "This weekly template has more than 7 workouts and cannot use weekday assignment in V1.";
    }
  
    const assignedWorkoutIds = weekdaySlots
      .filter((slot) => slot.workout)
      .map((slot) => slot.workout.id);
  
    const uniqueAssignedWorkoutIds = new Set(assignedWorkoutIds);
  
    if (assignedWorkoutIds.length !== templateWorkouts.length) {
      return "Each workout must be assigned exactly once.";
    }
  
    if (uniqueAssignedWorkoutIds.size !== assignedWorkoutIds.length) {
      return "Each workout must be assigned exactly once.";
    }
  
    const expectedWorkoutIds = templateWorkouts.map((workout) => workout.id);
  
    if (expectedWorkoutIds.some((id) => !uniqueAssignedWorkoutIds.has(id))) {
      return "Each workout must be assigned exactly once.";
    }
  
    return "";
  }, [templateWorkouts, weekdaySlots]);
  const selectedStartDateLabel = useMemo(() => formatDisplayDate(startDate), [startDate]);
  const visibleWeekOptions = useMemo(
    () => buildWeekOptionsForMonth(weekPickerMonth, minStartDate, startDate),
    [minStartDate, startDate, weekPickerMonth]
  );
  const canGoToPreviousMonth = useMemo(
    () => hasAllowedWeeksInMonth(addMonths(weekPickerMonth, -1), minStartDate),
    [minStartDate, weekPickerMonth]
  );
  const weekPickerMonthLabel = useMemo(
    () => formatMonthHeading(weekPickerMonth),
    [weekPickerMonth]
  );
  const visibleCandidateStartDates = useMemo(
    () => visibleWeekOptions.map((option) => option.value),
    [visibleWeekOptions]
  );
  const visibleCandidateKey = visibleCandidateStartDates.join(",");

  useEffect(() => {
    let cancelled = false;
    const preferredStartDate =
      startDateRef.current || initialPreferredStartDateRef.current;
    availabilityByStartDateRef.current = {};
    setAvailabilityByStartDate({});
    setAvailabilityStatus("loading");
    setAvailabilityError("");
    setIsVisibleAvailabilityLoading(false);

    findAvailableCycleStart({
      durationWeeks: programLength,
      minStartDate,
      preferredStartDate,
      isCancelled: () => cancelled,
    })
      .then((result) => {
        if (cancelled || !result) {
          return;
        }

        availabilityByStartDateRef.current = result.availability;
        setAvailabilityByStartDate(result.availability);
        setAvailabilityStatus("success");
        startDateRef.current = result.startDate;
        initialPreferredStartDateRef.current = result.startDate;
        setStartDate(result.startDate);
        setSubmitError("");
      })
      .catch((error) => {
        if (!cancelled) {
          availabilityByStartDateRef.current = {};
          setAvailabilityByStartDate({});
          setAvailabilityStatus("error");
          setAvailabilityError(
            error?.code === "AVAILABILITY_SEARCH_EXHAUSTED"
              ? error.message
              : AVAILABILITY_FAILURE_MESSAGE
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [availabilityRefreshToken, minStartDate, programLength]);

  useEffect(() => {
    if (availabilityStatus !== "success" || !visibleCandidateStartDates.length) {
      return undefined;
    }

    const missingStartDates = visibleCandidateStartDates.filter(
      (startDateValue) => !Object.prototype.hasOwnProperty.call(
        availabilityByStartDateRef.current,
        startDateValue
      )
    );
    if (!missingStartDates.length) {
      return undefined;
    }

    let cancelled = false;
    setIsVisibleAvailabilityLoading(true);
    getCycleStartAvailability({
      candidateStartDates: missingStartDates,
      durationWeeks: programLength,
    })
      .then((response) => {
        if (cancelled) {
          return;
        }

        const nextAvailability = mapVerifiedAvailability(response, missingStartDates);
        const mergedAvailability = {
          ...availabilityByStartDateRef.current,
          ...nextAvailability,
        };
        availabilityByStartDateRef.current = mergedAvailability;
        setAvailabilityByStartDate(mergedAvailability);
      })
      .catch(() => {
        if (!cancelled) {
          setIsVisibleAvailabilityLoading(false);
          setAvailabilityStatus("error");
          setAvailabilityError(AVAILABILITY_FAILURE_MESSAGE);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsVisibleAvailabilityLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [availabilityStatus, programLength, visibleCandidateKey, visibleCandidateStartDates]);

  const selectedStartAvailability = startDate
    ? availabilityByStartDate[startDate]
    : null;
  const isSelectedStartAvailable = Boolean(
    startDate &&
    availabilityStatus === "success" &&
    selectedStartAvailability?.hasConflict === false
  );

  const handleLengthChange = (weeks) => {
    setAvailabilityStatus("loading");
    setProgramLength(weeks);
    setSubmitError("");
  };

  const handleStartDateChange = (value) => {
    startDateRef.current = value;
    setStartDate(value);
    setSubmitError("");
  };

  const openWeekPicker = () => {
    const targetMonth = getStartOfMonthDate(startDate || minStartDate);
    setWeekPickerMonth((currentMonth) => (
      currentMonth.getFullYear() === targetMonth.getFullYear() &&
      currentMonth.getMonth() === targetMonth.getMonth()
        ? currentMonth
        : targetMonth
    ));
    setIsWeekPickerOpen(true);
  };

  const closeWeekPicker = () => {
    setIsWeekPickerOpen(false);
  };

  const handleSelectWeek = (value) => {
    if (
      availabilityStatus !== "success" ||
      availabilityByStartDate[value]?.hasConflict !== false
    ) {
      return;
    }
    handleStartDateChange(value);
    closeWeekPicker();
  };

  const handleMoveWorkout = (sourceIndex, direction) => {
    setWeekdaySlots((prev) => {
      const nextSlots = moveWorkoutWithDownwardPush(prev, sourceIndex, direction);
      return nextSlots || prev;
    });
    setSubmitError("");
  };

  const handleBack = async () => {
    if (isReturningToBuilderRef.current) {
      return;
    }

    const weeklyPlanParentId = draftMetadata.weeklyPlanParentId;
    if (!weeklyPlanParentId) {
      setBackError("Unable to reopen this Weekly draft. Retry from the published plan.");
      return;
    }

    isReturningToBuilderRef.current = true;
    setIsReturningToBuilder(true);
    setBackError("");

    try {
      await beginWeeklyHydrationTarget({
        weeklyPlanParentId,
        weeklyPlanVersionId: null,
      });
      const response = await openOrCreateWeeklyPlanEditDraft(weeklyPlanParentId);
      const isEditableDraft =
        response?.weeklyPlanParentId === weeklyPlanParentId &&
        Boolean(response?.weeklyPlanVersionId) &&
        String(response?.status || "").toUpperCase() === "DRAFT";

      if (!isEditableDraft) {
        throw new Error("The Weekly draft response was not editable.");
      }

      hydrateWeeklyProgramDraft(response);
      navigate(getManualBuilderPath());
    } catch {
      setBackError(
        "Unable to reopen an editable Weekly draft. Retry to return safely."
      );
    } finally {
      isReturningToBuilderRef.current = false;
      setIsReturningToBuilder(false);
    }
  };

  const handleConvert = async () => {
    if (!draftMetadata.weeklyPlanParentId || isSubmitting || isReturningToBuilder) {
      return;
    }

    setSubmitError("");
    setIsSubmitting(true);

    try {
      if (!startDate) {
        throw new Error("Please choose a valid start date.");
      }

      if (!isSelectedStartAvailable) {
        throw new Error(
          "Choose a start week after Cycle availability has been confirmed."
        );
      }

      if (!isMondayDateInput(startDate)) {
        throw new Error("Start date must be a Monday.");
      }

      if (weekdayAssignmentError) {
        throw new Error(weekdayAssignmentError);

      }

      const workoutOrderIndexById = new Map(
        templateWorkouts.map((workout, index) => [
          workout.id,
          workout.orderIndex ?? index + 1,
        ])
      );
      const response = await createCycleFromWeeklyPlan({
        weeklyPlanParentId: draftMetadata.weeklyPlanParentId,
        name: programName,
        startDate,
        durationWeeks: Number(programLength),
        workoutDayAssignments: weekdaySlots
          .filter((slot) => slot.workout)
          .map((slot) => ({
            workoutOrderIndex: workoutOrderIndexById.get(slot.workout.id),
            scheduledDay: slot.day,
          })),
      });

      await beginHydrationTarget({ cycleId: response.cycleId, planId: null });
      hydrateProgramDraft(response);
      navigate(getCycleBuilderPath(response.cycleId));
    } catch (error) {
      setSubmitError(error.message || "Unable to convert this weekly plan");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* The exhausted-search failure is the ONLY availability error the current
   * implementation reports with its own explanatory copy; every other failure
   * reuses AVAILABILITY_FAILURE_MESSAGE. Deriving the distinction from the
   * message the existing catch already chose keeps the async state shape
   * untouched while letting the truthful "no start week exists" case render as
   * a canonical EmptyState instead of a retry line. */
  const hasNoAvailableStartWeek =
    availabilityStatus === "error" && availabilityError !== AVAILABILITY_FAILURE_MESSAGE;
  const isCheckingAvailability =
    availabilityStatus === "loading" || isVisibleAvailabilityLoading;

  return (
    <DesignV2Scope className="lz-manual-convert" data-testid="manual-convert-root">
      <StackHeader
        title="Create Program"
        backLabel="Back"
        backBusy={isReturningToBuilder}
        onBack={handleBack}
      />

      <main className="lz-manual-convert__content">
        {isReturningToBuilder && (
          <p className="lz-manual-convert__status" role="status">
            Opening editable Weekly draft...
          </p>
        )}

        {backError && (
          <div className="lz-manual-convert__alert" role="alert">
            <p className="lz-manual-convert__alert-copy">{backError}</p>
            <Button
              className="lz-manual-convert__alert-action"
              variant="ghost"
              onClick={handleBack}
              disabled={isReturningToBuilder}
            >
              Retry
            </Button>
          </div>
        )}

        <Card className="lz-manual-convert__source" tone="raised">
          <span className="lz-manual-convert__source-mark" aria-hidden="true">
            <ConvertIcon />
          </span>
          <div className="lz-manual-convert__source-copy">
            <p className="lz-manual-convert__source-eyebrow">Multi week program</p>
            <h2 className="lz-manual-convert__source-name">{programName}</h2>
          </div>
        </Card>

        <section
          className="lz-manual-convert__section"
          aria-labelledby="manual-convert-timeline-title"
        >
          <h3 className="lz-manual-convert__section-title" id="manual-convert-timeline-title">
            Timeline Settings
          </h3>

          <div className="lz-manual-convert__control">
            <span className="lz-manual-convert__control-label">Start Date</span>

            <button
              type="button"
              className="lz-manual-convert__start-trigger"
              onClick={openWeekPicker}
              aria-label="Select start week"
              aria-haspopup="dialog"
              aria-expanded={isWeekPickerOpen}
            >
              <span className="lz-manual-convert__start-trigger-mark" aria-hidden="true">
                <CalendarIcon />
              </span>
              <span className="lz-manual-convert__start-trigger-value">
                {selectedStartDateLabel}
              </span>
              <span className="lz-manual-convert__start-trigger-caret" aria-hidden="true">
                <ChevronIcon direction="down" />
              </span>
            </button>

            <p className="lz-manual-convert__control-hint">
              Choose a start week. The selected start date remains the Monday of that week.
            </p>

            {isCheckingAvailability && (
              <p className="lz-manual-convert__control-status" role="status">
                Checking Cycle availability...
              </p>
            )}

            {hasNoAvailableStartWeek ? (
              <EmptyState
                className="lz-manual-convert__no-weeks"
                titleAs="p"
                icon={<CalendarIcon />}
                title="No available start week"
                body={availabilityError}
                action={
                  <Button
                    variant="secondary"
                    onClick={() => setAvailabilityRefreshToken((value) => value + 1)}
                  >
                    Retry
                  </Button>
                }
              />
            ) : availabilityStatus === "error" ? (
              <div className="lz-manual-convert__alert" role="alert">
                <p className="lz-manual-convert__alert-copy">{availabilityError}</p>
                <Button
                  className="lz-manual-convert__alert-action"
                  variant="ghost"
                  onClick={() => setAvailabilityRefreshToken((value) => value + 1)}
                >
                  Retry
                </Button>
              </div>
            ) : null}
          </div>

          <div className="lz-manual-convert__control">
            <span className="lz-manual-convert__control-label" id="manual-convert-length-label">
              Program Length
            </span>

            <div
              className="lz-manual-convert__length-options"
              role="group"
              aria-labelledby="manual-convert-length-label"
            >
              {[6, 7, 8].map((weeks) => (
                <Chip
                  key={weeks}
                  className="lz-manual-convert__length-option"
                  selected={programLength === weeks}
                  onClick={() => handleLengthChange(weeks)}
                >
                  {weeks} weeks
                </Chip>
              ))}
            </div>

            {!([6, 7, 8].includes(programLength)) && (
              <p className="lz-manual-convert__control-note">
                Custom length: {programLength} weeks
              </p>
            )}
          </div>

          <div className="lz-manual-convert__control">
            <div className="lz-manual-convert__control-label-row">
              <span className="lz-manual-convert__control-label">
                End Date (Auto-calculated)
              </span>
              <Badge tone="info">Derived</Badge>
            </div>

            <div className="lz-manual-convert__derived">
              <span className="lz-manual-convert__derived-mark" aria-hidden="true">
                <CalendarCheckIcon />
              </span>
              <span className="lz-manual-convert__derived-value">{endDate || "--"}</span>
            </div>
          </div>
        </section>

        <section
          className="lz-manual-convert__section"
          aria-labelledby="manual-convert-schedule-title"
        >
          <div className="lz-manual-convert__section-heading">
            <h3 className="lz-manual-convert__section-title" id="manual-convert-schedule-title">
              Weekly Schedule
            </h3>
            <p className="lz-manual-convert__section-copy">
              Assign each workout to a weekday before this template is duplicated across all weeks.
            </p>
          </div>

          <ul className="lz-manual-convert__days">
            {weekdaySlots.map((slot, index) => {
              const canMoveUp = Boolean(moveWorkoutWithDownwardPush(weekdaySlots, index, -1));
              const canMoveDown = Boolean(moveWorkoutWithDownwardPush(weekdaySlots, index, 1));

              return (
                <li className="lz-manual-convert__day-item" key={slot.day}>
                  <Card className="lz-manual-convert__day" padding="sm">
                    <span className="lz-manual-convert__day-mark">
                      <span className="lz-manual-convert__day-initial" aria-hidden="true">
                        {slot.shortLabel}
                      </span>
                      <span className="lz-manual-convert__day-abbr">
                        {slot.fullLabel.slice(0, 3)}
                      </span>
                    </span>

                    {slot.workout ? (
                      <>
                        <span className="lz-manual-convert__day-copy">
                          <span className="lz-manual-convert__day-name">
                            {slot.workout.name}
                          </span>
                          <span className="lz-manual-convert__day-meta">
                            Workout {slot.workout.orderIndex ?? "—"}
                          </span>
                        </span>

                        <span className="lz-manual-convert__day-actions">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleMoveWorkout(index, -1)}
                            disabled={!canMoveUp}
                            aria-label={`Move ${slot.workout.name} up`}
                          >
                            <ArrowIcon direction="up" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleMoveWorkout(index, 1)}
                            disabled={!canMoveDown}
                            aria-label={`Move ${slot.workout.name} down`}
                          >
                            <ArrowIcon direction="down" />
                          </Button>
                        </span>
                      </>
                    ) : (
                      <span className="lz-manual-convert__day-rest">Rest day</span>
                    )}
                  </Card>
                </li>
              );
            })}
          </ul>

          {weekdayAssignmentError && (
            <div className="lz-manual-convert__alert" role="alert">
              <p className="lz-manual-convert__alert-copy">{weekdayAssignmentError}</p>
            </div>
          )}
        </section>

        <Card className="lz-manual-convert__note" tone="raised">
          <span className="lz-manual-convert__note-mark" aria-hidden="true">
            <InfoIcon />
          </span>
          <p className="lz-manual-convert__note-copy">{previewText}</p>
        </Card>

        {submitError && (
          <div className="lz-manual-convert__alert" role="alert">
            <p className="lz-manual-convert__alert-copy">{submitError}</p>
          </div>
        )}
      </main>

      <StickyBottomActions className="lz-manual-convert__actions">
        <Button
          className="lz-manual-convert__submit"
          size="lg"
          onClick={handleConvert}
          disabled={
            isSubmitting ||
            isReturningToBuilder ||
            !draftMetadata.weeklyPlanParentId ||
            Boolean(weekdayAssignmentError) ||
            !isSelectedStartAvailable
          }
          isLoading={isSubmitting}
          loadingLabel="Creating..."
        >
          Convert to Multi week
        </Button>
      </StickyBottomActions>

      <Sheet
        open={isWeekPickerOpen}
        onClose={closeWeekPicker}
        title="Select a start week"
        closeLabel="Close week picker"
        surfaceClassName="lz-manual-convert__week-picker"
      >
        <div className="lz-manual-convert__month-nav">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setWeekPickerMonth((prev) => addMonths(prev, -1))}
            disabled={!canGoToPreviousMonth}
            aria-label="Previous month"
          >
            <ChevronIcon direction="left" />
          </Button>

          <p className="lz-manual-convert__month-label" aria-live="polite">
            {weekPickerMonthLabel}
          </p>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setWeekPickerMonth((prev) => addMonths(prev, 1))}
            aria-label="Next month"
          >
            <ChevronIcon direction="right" />
          </Button>
        </div>

        <div className="lz-manual-convert__weeks">
          {visibleWeekOptions.length === 0 ? (
            <EmptyState
              className="lz-manual-convert__weeks-empty"
              titleAs="p"
              title="No start weeks in this month"
              body="Move forward a month to pick a start week."
            />
          ) : (
            visibleWeekOptions.map((option) => {
              const isSelected = option.value === startDate;
              const optionAvailability = availabilityByStartDate[option.value];
              const isUnavailable =
                availabilityStatus !== "success" ||
                optionAvailability?.hasConflict !== false;
              /* Availability is only ever KNOWN when the backend has answered
               * for this exact Monday with a boolean. Anything else is unknown
               * and must never read as available. */
              const isResolved = typeof optionAvailability?.hasConflict === "boolean";
              const hasConflict = optionAvailability?.hasConflict === true;

              return (
                <SelectableCard
                  key={option.value}
                  className="lz-manual-convert__week"
                  onClick={() => handleSelectWeek(option.value)}
                  disabled={isUnavailable}
                  aria-disabled={isUnavailable}
                  selected={isSelected}
                  title={option.label}
                  description={
                    option.isSelectedOutsideMonth
                      ? "Selected week"
                      : optionAvailability?.hasConflict
                        ? "Overlaps an existing Cycle"
                        : availabilityStatus !== "success"
                          ? "Checking availability"
                      : option.isCurrentWeek
                        ? "Current week"
                        : option.monthLabel
                  }
                  trailing={
                    <span className="lz-manual-convert__week-trailing">
                      {hasConflict ? <StatusPill tone="danger">Conflict</StatusPill> : null}
                      {!isResolved ? (
                        <Skeleton
                          className="lz-manual-convert__week-pending"
                          shape="text"
                          width="4.5rem"
                          height="1.25rem"
                        />
                      ) : null}
                      {option.isCurrentWeek ? <Badge>Current</Badge> : null}
                      {isSelected ? (
                        <span className="lz-manual-convert__week-check" aria-hidden="true">
                          ✓
                        </span>
                      ) : null}
                    </span>
                  }
                />
              );
            })
          )}
        </div>
      </Sheet>
    </DesignV2Scope>
  );
}
